import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, HelpCircle } from 'lucide-react';

import type {
  SanitizedQuizQuestion,
  StageQuizView,
} from 'features/courier-training/services/courierTrainingService';
import type { SupportedLanguage } from 'shared/i18n/i18n';

interface StageQuizProps {
  quiz: StageQuizView;
  language: SupportedLanguage;
  disabled: boolean;
  submitting: boolean;
  onSubmit: (answers: Record<string, number>) => void;
}

const OPTION_LABELS = ['A', 'B', 'C', 'D'];

const StageQuiz: React.FC<StageQuizProps> = ({
  quiz,
  language,
  disabled,
  submitting,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Record<string, number>>(
    quiz.savedAnswers ?? {},
  );

  const locked = quiz.answered || disabled;
  const allAnswered = quiz.questions.every((q) => selected[q.id] != null);

  const pick = (questionId: string, index: number) => {
    if (locked) return;
    setSelected((prev) => ({ ...prev, [questionId]: index }));
  };

  const text = (
    localized: SanitizedQuizQuestion['prompt'],
  ): string => localized[language] ?? localized.ar;

  return (
    <div className="border-t border-gray-100 bg-gray-50/60 p-4 sm:p-5">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
        <HelpCircle size={16} className="text-[#bf0035]" />
        {t('portal.training.quiz.title')}
      </div>

      <div className="flex flex-col gap-4">
        {quiz.questions.map((question, qIdx) => (
          <div
            key={question.id}
            className="rounded-xl border border-gray-200 bg-white p-4"
          >
            <p className="mb-3 text-sm font-semibold text-gray-900">
              <span className="text-[#bf0035]">{qIdx + 1}.</span>{' '}
              {text(question.prompt)}
            </p>
            <div className="flex flex-col gap-2">
              {question.options.map((option, oIdx) => {
                const isSelected = selected[question.id] === oIdx;
                return (
                  <button
                    key={oIdx}
                    type="button"
                    disabled={locked}
                    onClick={() => pick(question.id, oIdx)}
                    className={[
                      'flex items-center gap-3 rounded-lg border p-3 text-start text-sm transition-all',
                      isSelected
                        ? 'border-[#bf0035] bg-[#bf0035]/[0.06] text-gray-900'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300',
                      locked ? 'cursor-default opacity-90' : 'cursor-pointer',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-bold',
                        isSelected
                          ? 'border-[#bf0035] bg-[#bf0035] text-white'
                          : 'border-gray-300 text-gray-500',
                      ].join(' ')}
                    >
                      {OPTION_LABELS[oIdx]}
                    </span>
                    <span className="flex-1">{text(option)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {quiz.answered ? (
        <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#bf0035]">
          <CheckCircle2 size={16} />
          {t('portal.training.quiz.submitted')}
        </div>
      ) : (
        <button
          type="button"
          disabled={locked || !allAnswered || submitting}
          onClick={() => onSubmit(selected)}
          className="btn-primary mt-4 inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting
            ? t('portal.training.quiz.submitting')
            : t('portal.training.quiz.submit')}
        </button>
      )}
    </div>
  );
};

export default StageQuiz;
